import { colorsByMedium } from 'modules/messagingInsights/constants';
import {
  ColChartMediumData,
  ColChartMetricData,
} from 'modules/messagingInsights/components/CrossMediumTimeSeries/types';
import { AggOverallMetrics, AggMetricDataByMedium } from 'modules/messagingInsights/types';
import {
  HighLevelMediumAgnosticMetrics,
  MediumAgnosticLabel,
} from 'modules/messagingInsights/constants';
import { BaseOption } from 'ui/selects';
import { colors, fonts, fontSizes } from 'ui/theme';

type ColChartMetricDataWithValue = ColChartMetricData & { value: number };

/**
 * Create highcharts-formatted time series data for each medium belonging to a high-level metric
 * @param overallMetricData
 * @returns { ColChartMediumData[] } an array of objects with the name of each medium and the
 * corresponding chart data
 */
export const getChartDataPerMedium = (
  overallMetricData: {
    value?: number;
    mediums?: AggMetricDataByMedium;
    histogram?: {
      buckets: { time: number; value: number }[];
    };
  },
  metric: string,
) => {
  const dataByMedium: AggMetricDataByMedium = overallMetricData.mediums || {};
  const chartData = HighLevelMediumAgnosticMetrics.has(metric)
    ? [
        /** for medium-agnostic metrics (e.g. Total Unsubscribes, Revenue etc.), we want to use the histogram data
         * nested directly under the metric itself instead of the repeated/duplicate data belonging to the individual mediums */
        {
          name: MediumAgnosticLabel,
          data: overallMetricData.histogram?.buckets.map((data) => [data.time, data.value]),
          color: colors.red50,
        },
      ]
    : Object.keys(dataByMedium).reduce((acc: ColChartMediumData[], medium) => {
        // Hide mediums that do not have any histogram data
        if (!dataByMedium[medium].histogram || !dataByMedium[medium].value) {
          return acc;
        }
        return [
          ...acc,
          {
            name: medium,
            data: dataByMedium[medium].histogram?.buckets.map((data) => [data.time, data.value]),
            color: colorsByMedium[medium],
          },
        ];
      }, []);
  return chartData;
};

/**
 * Create highcharts-formatted time series chart data for each x-medium metric (1 chart for each overall metric)
 * @param overallMetrics aggregate metric data received from our BE endpoint
 * @returns parsed chart data by metric
 */
export const getCrossMediumChartData = (
  overallMetrics: AggOverallMetrics,
): Record<string, ColChartMetricDataWithValue> => {
  return Object.entries(overallMetrics).reduce((acc, [metricName, metricData]) => {
    if (!metricName.startsWith('Total ')) {
      // only include metrics that are total values, not percentage rates
      return acc;
    }
    return {
      ...acc,
      [metricName]: {
        metric: metricName,
        value: metricData.value,
        data: getChartDataPerMedium(metricData, metricName),
      },
    };
  }, {});
};

/**
 * Map chart data to metric options for dropdown selection
 * @param overallMetrics Record of chart data by metric
 * @returns Array of metric BaseOption with id, label, and value
 */
export const getMetricOptions = (
  overallMetrics: Record<string, ColChartMetricDataWithValue>,
): BaseOption[] => {
  return (
    Object.keys(overallMetrics).map((metric) => ({
      id: metric,
      label: metric,
      value: metric,
    })) || []
  );
};

/**
 * Generate timeseries chart options
 * @returns
 */
export const getMetricsChartOptions = () => {
  return {
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      height: 165,
      marginBottom: 24,
      style: {
        fontFamily: fonts.sansSerif,
        fontSize: fontSizes.s14,
      },
    },
    tooltip: {
      useHTML: true,
      style: {
        pointerEvents: 'auto',
      },
      // @ts-ignore
      pointFormatter() {
        // @ts-ignore
        const medium = this.series.name;

        // @ts-ignore
        return `<br/><span style="color:${this.color}">\u25CF</span> ${medium}: ${
          // @ts-ignore
          this.y
        }`;
      },
    },
    plotOptions: {
      series: {
        marker: {
          symbol: 'circle',
        },
      },
    },
    legend: {
      // Hide legend below graph
      enabled: false,
    },
  };
};
